import { OrdersRepository } from '@lib/api/repository/orders/orders.repository';
import {
  AdminOrder,
  DisputeEvidence,
  DisputeMetrics,
  OrderComment,
  OrderNotification,
  OrderUpdateInput,
  SellerCancellationInput,
  StoreReview,
} from '@lib/types';
import { AdminPage, AdminPageFilters } from '@lib/types/admin-page';

import { Requester } from '../clients';

import {
  AdminOrderDTO,
  AdminOrderStatisticsDTO,
  PurchaseOrderDTO,
  PurchaseOrderDetailsDTO,
  SalesOrderDTO,
  SalesOrderDetailsDTO,
  createAdminOrder,
  createAdminOrderStatistics,
  createPurchaseOrder,
  createPurchaseOrderDetails,
  createSalesOrder,
  createSalesOrderDetails,
} from './models';

const isPurchaseOrder = (
  order: PurchaseOrderDTO | SalesOrderDTO,
): order is PurchaseOrderDTO => Object.hasOwn(order, 'seller');

export default class OrdersAPI implements OrdersRepository {
  async getAdminStatistics(year: number, signal?: AbortSignal) {
    const response = await Requester.get<AdminOrderStatisticsDTO>(
      '/marketplace/admin/orders/statistics',
      { params: { year }, signal },
    );

    return createAdminOrderStatistics(response.data);
  }

  async listAdminPage(
    filters: AdminPageFilters,
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminOrder>> {
    const result = (
      await Requester.get<AdminPage<AdminOrderDTO>>(
        '/marketplace/admin/orders/page',
        { params: { ...filters }, signal },
      )
    ).data;

    return { items: result.items.map(createAdminOrder), total: result.total };
  }

  async listPurchases(signal?: AbortSignal) {
    const response = await Requester.get<PurchaseOrderDTO[]>(
      '/marketplace/orders/purchases',
      { signal },
    );

    return response.data.map(createPurchaseOrder);
  }

  async listSales(signal?: AbortSignal) {
    const response = await Requester.get<SalesOrderDTO[]>(
      '/marketplace/orders/sales',
      { signal },
    );

    return response.data.map(createSalesOrder);
  }

  async countPendingShipments(signal?: AbortSignal) {
    const response = await Requester.get<{ count: number }>(
      '/marketplace/orders/sales/pending',
      { signal },
    );

    return response.data.count;
  }

  async listAdmin(signal?: AbortSignal) {
    const response = await Requester.get<AdminOrderDTO[]>(
      '/marketplace/admin/orders',
      { signal },
    );

    return response.data.map(createAdminOrder);
  }

  async getDisputeMetrics(signal?: AbortSignal) {
    return (
      await Requester.get<DisputeMetrics>(
        '/marketplace/admin/orders/dispute-metrics',
        { signal },
      )
    ).data;
  }

  async listNotifications(signal?: AbortSignal) {
    return (
      await Requester.get<OrderNotification[]>(
        '/marketplace/orders/notifications',
        { signal },
      )
    ).data;
  }

  async markAllNotificationsRead() {
    await Requester.patch('/marketplace/orders/notifications/read-all');
  }

  async markNotificationRead(id: string) {
    await Requester.patch(`/marketplace/orders/notifications/${id}/read`);
  }

  async findPurchase(id: string, signal?: AbortSignal) {
    const response = await Requester.get<PurchaseOrderDetailsDTO>(
      `/marketplace/orders/purchases/${id}`,
      { signal },
    );

    return createPurchaseOrderDetails(response.data);
  }

  async findSale(id: string, signal?: AbortSignal) {
    const response = await Requester.get<SalesOrderDetailsDTO>(
      `/marketplace/orders/sales/${id}`,
      { signal },
    );

    return createSalesOrderDetails(response.data);
  }

  async findAdmin(id: string, signal?: AbortSignal) {
    const response = await Requester.get<SalesOrderDetailsDTO>(
      `/marketplace/admin/orders/${id}`,
      { signal },
    );

    return createSalesOrderDetails(response.data);
  }

  async cancelBySeller(id: string, input: SellerCancellationInput) {
    const response = await Requester.post<SalesOrderDTO>(
      `/marketplace/orders/${id}/cancel`,
      {
        body: {
          cancellationCategory: input.cancellationCategory,
          restockItems: input.restockItems,
          notes: input.notes,
        },
      },
    );

    return createSalesOrder(response.data);
  }

  async update(id: string, input: OrderUpdateInput) {
    const response = await Requester.patch<PurchaseOrderDTO | SalesOrderDTO>(
      `/marketplace/orders/${id}`,
      { body: input },
    );

    return isPurchaseOrder(response.data)
      ? createPurchaseOrder(response.data)
      : createSalesOrder(response.data);
  }

  async updateAdmin(id: string, input: OrderUpdateInput) {
    const response = await Requester.patch<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/status`,
      { body: input },
    );

    return createAdminOrder(response.data);
  }

  async indicateDelivery(id: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    const response = await Requester.post<SalesOrderDTO>(
      `/marketplace/orders/${id}/delivery-indication`,
      { body },
    );

    return createSalesOrder(response.data);
  }

  async downloadDeliveryProof(id: string) {
    const response = await Requester.download(
      `/marketplace/orders/${id}/delivery-proof`,
    );

    return response.data;
  }

  async confirmReceived(id: string) {
    const response = (
      await Requester.post<PurchaseOrderDTO>(
        `/marketplace/orders/${id}/confirm-received`,
      )
    ).data;

    return createPurchaseOrder(response);
  }

  async justifyNonShipment(id: string, justification: string) {
    const response = await Requester.post<SalesOrderDTO>(
      `/marketplace/orders/${id}/non-shipment-justification`,
      { body: { justification } },
    );

    return createSalesOrder(response.data);
  }

  async openDispute(
    id: string,
    reason: string,
    description: string,
    requestedOutcome: 'refund' | 'fulfillment',
  ) {
    const response = (
      await Requester.post<PurchaseOrderDTO>(
        `/marketplace/orders/${id}/disputes`,
        { body: { reason, description, requestedOutcome } },
      )
    ).data;

    return createPurchaseOrder(response);
  }

  async respondToDispute(id: string, response: string, proposal?: string) {
    const result = await Requester.post<SalesOrderDTO>(
      `/marketplace/orders/${id}/disputes/respond`,
      { body: { response, proposal } },
    );

    return createSalesOrder(result.data);
  }

  async decideDisputeProposal(id: string, accept: boolean) {
    const result = await Requester.post<PurchaseOrderDTO>(
      `/marketplace/orders/${id}/disputes/proposal`,
      { body: { decision: accept ? 'accept' : 'reject' } },
    );

    return createPurchaseOrder(result.data);
  }

  async claimDispute(id: string) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/disputes/claim`,
    );

    return createAdminOrder(result.data);
  }

  async addDisputeInternalNote(id: string, note: string) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/disputes/internal-note`,
      { body: { note } },
    );

    return createAdminOrder(result.data);
  }

  async requestDisputeInfo(
    id: string,
    target: 'buyer' | 'seller',
    note: string,
  ) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/disputes/request-info`,
      { body: { target, note } },
    );

    return createAdminOrder(result.data);
  }

  async resolveDispute(
    id: string,
    resolution: 'buyer' | 'seller' | 'return',
    note: string,
  ) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/disputes/resolve`,
      { body: { resolution, note } },
    );

    return createAdminOrder(result.data);
  }

  async markReturnShipped(id: string, trackingCode: string) {
    const result = await Requester.post<PurchaseOrderDTO>(
      `/marketplace/orders/${id}/disputes/return-shipped`,
      { body: { trackingCode } },
    );

    return createPurchaseOrder(result.data);
  }

  async retryRefund(id: string) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/refund/retry`,
    );

    return createAdminOrder(result.data);
  }

  async confirmPhysicalReturn(id: string) {
    const result = await Requester.post<AdminOrderDTO>(
      `/marketplace/admin/orders/${id}/return-received`,
    );

    return createAdminOrder(result.data);
  }

  async listDisputeEvidence(id: string, signal?: AbortSignal) {
    return (
      await Requester.get<DisputeEvidence[]>(
        `/marketplace/orders/${id}/disputes/evidence`,
        { signal },
      )
    ).data;
  }

  async uploadDisputeEvidence(id: string, file: File) {
    const body = new FormData();
    body.append('file', file);
    return (
      await Requester.post<DisputeEvidence>(
        `/marketplace/orders/${id}/disputes/evidence`,
        { body },
      )
    ).data;
  }

  async downloadDisputeEvidence(
    id: string,
    evidenceId: string,
    signal?: AbortSignal,
  ) {
    return (
      await Requester.download(
        `/marketplace/orders/${id}/disputes/evidence/${evidenceId}`,
        { signal },
      )
    ).data;
  }

  async listComments(id: string, signal?: AbortSignal) {
    return (
      await Requester.get<OrderComment[]>(
        `/marketplace/orders/${id}/comments`,
        { signal },
      )
    ).data;
  }

  async addComment(id: string, message: string, image?: File) {
    if (image) {
      const body = new FormData();
      body.append('message', message);
      body.append('file', image);

      return (
        await Requester.post<OrderComment>(
          `/marketplace/orders/${id}/comments/image`,
          { body },
        )
      ).data;
    }

    return (
      await Requester.post<OrderComment>(`/marketplace/orders/${id}/comments`, {
        body: { message },
      })
    ).data;
  }

  async downloadCommentImage(
    id: string,
    commentId: string,
    signal?: AbortSignal,
  ) {
    return (
      await Requester.download(
        `/marketplace/orders/${id}/comments/${commentId}/image`,
        { signal },
      )
    ).data;
  }

  async createReview(id: string, rating: number, comment: string) {
    return (
      await Requester.post<StoreReview>(`/marketplace/orders/${id}/review`, {
        body: { rating, comment },
      })
    ).data;
  }

  async simulateSandboxPayment(id: string) {
    await Requester.post(`/marketplace/admin/payments/orders/${id}/simulate`);
  }
}
